import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { styled } from '@linaria/react'
import { MinimalSkipLink, MinimalSkeleton } from '@ovasabi/ui-minimal'
import { minimalVars as v } from '@ovasabi/ui-minimal/tokens'
import { BrandMascot, PinIcon } from '../../components/icons'
import { KitchenBadge } from '../../components/chow/KitchenBadge'
import { ChowThumb } from './ChowThumb'
import { usePublicChefs, usePublicDishes } from '../../lib/discover'
import { dishPath, kitchenPath } from '../../lib/slug'
import { useDeliveryLocation } from '../../lib/deliveryLocation'
import { distanceNote } from '../../lib/places'
import { LandingStyle as Style } from './LandingStyle'
import { DeliveryLocationModal } from './DeliveryLocationModal'
import { FoodNavigation } from './FoodNavigation'
import { PromoDealsBanner } from './PromoDealsBanner'
import { chefPhoto, dishPhoto } from './dishImages'
import { PublicFooter, PublicNav } from './PublicLayout'

const STEPS = [
  {
    title: 'Choose a chef',
    body: 'See the home chefs in your area and what each one makes.',
  },
  {
    title: 'Order and pay',
    body: 'Pay by card or bank transfer. Your chef prepares the food for you.',
  },
  {
    title: 'Collect it',
    body: 'Pick it up from the kitchen now, or at a time that suits you.',
  },
]

const CATEGORIES = [
  { id: 'all', label: 'All Dishes' },
  { id: 'rice', label: 'Rice Specialties' },
  { id: 'soups', label: 'Soups & Swallows' },
  { id: 'grill', label: 'Grills & Suya' },
  { id: 'pastry', label: 'Pastries & Bakes' },
  { id: 'breakfast', label: 'Breakfast' },
  { id: 'drinks', label: 'Drinks & Coolers' },
] as const

const formatNairaMinor = (minor: number) =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 2,
  }).format(Math.max(0, Number(minor || 0)) / 100)

/*
 * Kitchen tile foot. The open and closed greens stay hand-set: the success token
 * resolves to a lighter green, so a token would shift the status colour.
 */
const ChefFootStyle = {
  Open: styled.p<{ $open: boolean }>`
    margin: 0;
    font-size: ${v.typography.captionSize};
    color: ${({ $open }) => ($open ? '#00875A' : '#777777')};
    font-weight: ${v.typography.weightBold};
  `,
  Link: styled.strong`
    font-size: ${v.typography.captionSize};
    color: #e31837;
  `,
}

export const LandingPage = () => {
  const location = useDeliveryLocation()
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [locationModalOpen, setLocationModalOpen] = useState(false)
  const { dishes, loading: dishesLoading } = usePublicDishes(16)
  const { chefs, loading: chefsLoading } = usePublicChefs(8, location.cityRef || location.area)

  const filteredDishes = useMemo(() => {
    if (selectedCategory === 'all') return dishes
    return dishes.filter((dish) => {
      const cat = (dish.category || '').toLowerCase()
      return cat === selectedCategory || (selectedCategory === 'grill' && cat === 'protein')
    })
  }, [dishes, selectedCategory])
  const heroMeals = useMemo(
    () => dishes.filter((dish) => dish.status !== 'sold_out').slice(0, 3),
    [dishes]
  )
  const leadMeal = heroMeals[0]

  return (
    <Style.Page>
      <MinimalSkipLink />
      <PublicNav
        extra={
          <Style.DeliverPill variant="secondary"
            type="button"
            onClick={() => setLocationModalOpen(true)}
            aria-label={`Current area: ${location.area || 'Abuja, FCT'}. Click to change.`}
          >
            <PinIcon size="sm" /><span>Pickup near</span>
            <strong>{location.area || 'Abuja, FCT'}</strong>
          </Style.DeliverPill>
        }
      />

      <DeliveryLocationModal
        open={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        location={location}
      />
      <Style.Main id="main-content" tabIndex={-1}>
        <Style.Hero
          enter={false}
          anchor="right-visual"
          visualMode="side"
          mediaAspectRatio="8 / 9"
          minHeight="auto"
          eyebrow="Made by chefs near you"
          title={
            <>
              Homemade food,
              <br />
              ready for <Style.Accent>pickup.</Style.Accent>
            </>
          }
          description="Order jollof, egusi, suya and more from home chefs in your area. They make it, you collect it when it’s ready."
          actions={
            <>
              <Style.Cta to="/register?role=customer">Order now →</Style.Cta>
              <Style.Cta data-variant="ghost" to="/register?role=chef">
                Sell your cooking
              </Style.Cta>
            </>
          }
          visual={
            leadMeal ? (
              <Style.HeroMeals aria-label="Popular meals">
                <Style.HeroMealLead to={dishPath(leadMeal)}>
                  <ChowThumb
                    src={dishPhoto(leadMeal.image, leadMeal.category, leadMeal.name)}
                    alt={leadMeal.name}
                    name={leadMeal.name}
                    category={leadMeal.category}
                  />
                  <span>
                    <strong>{leadMeal.name}</strong>
                    <small>
                      {formatNairaMinor(leadMeal.price_minor)} · {leadMeal.chef}
                    </small>
                  </span>
                </Style.HeroMealLead>
                <Style.HeroMealList>
                  {heroMeals.slice(1, 3).map((dish) => (
                    <Style.HeroMealMini key={dish.id} to={dishPath(dish)}>
                      <ChowThumb
                        src={dishPhoto(dish.image, dish.category, dish.name)}
                        alt={dish.name}
                        name={dish.name}
                        category={dish.category}
                        thumbnailWidth={128}
                      />
                      <span>
                        <strong>{dish.name}</strong>
                        <small>{formatNairaMinor(dish.price_minor)}</small>
                      </span>
                    </Style.HeroMealMini>
                  ))}
                </Style.HeroMealList>
                <Style.HeroBadge>
                  <span className="star">★</span>
                  <span>Popular meals near {location.area || 'Abuja'}</span>
                </Style.HeroBadge>
              </Style.HeroMeals>
            ) : (
              <Style.HeroPhotoWrap>
                <Style.HeroPhoto
                  src="/seed/thumbs/dish/jollof-768.webp"
                  width="960"
                  height="1080"
                  alt="Jollof rice, grilled chicken and golden plantain"
                  fetchPriority="high"
                  decoding="async"
                />
                <Style.HeroBadge>
                  <span className="star">★</span>
                  <span>Popular meals near {location.area || 'Abuja'}</span>
                </Style.HeroBadge>
              </Style.HeroPhotoWrap>
            )
          }
        >
          <Style.Trust>
            <span>Made by local chefs</span>
            <span aria-hidden="true">·</span>
            <Link to="/kitchens">
              {chefs.length
                ? `${chefs.length} local kitchen${chefs.length === 1 ? '' : 's'}`
                : 'Local kitchens'}
            </Link>
            <span aria-hidden="true">·</span>
            <span>Kitchen pickup</span>
          </Style.Trust>
        </Style.Hero>

        <PromoDealsBanner />

        <Style.Section
          eyebrow="Eat today or plan ahead"
          title="Order for today, or plan your week."
          description="Pick a dish for now, or set your meals for the whole week in one go."
        >
          <FoodNavigation expanded publicLinks />
        </Style.Section>

        <Style.PlanBand>
          <Style.PlanBandCopy>
            <h3>Plan the week in one sitting.</h3>
            <p>
              Choose meals for the days ahead. Your chef knows in advance and has each one ready at the time you picked.
            </p>
            <Style.PlanDualGrid>
              <Style.PlanDualCard>
                <strong>🍲 For households</strong>
                <span>Choose lunches and dinners for the week. Pay as you go, and collect each meal when it’s ready.</span>
              </Style.PlanDualCard>
              <Style.PlanDualCard>
                <strong>👩🏾‍🍳 For kitchens</strong>
                <span>See orders days ahead, so you buy only what you need and cook in batches.</span>
              </Style.PlanDualCard>
            </Style.PlanDualGrid>
            <Style.PlanBandActions>
              <Style.Cta to="/plans">Plan my week →</Style.Cta>
              <Style.Cta data-variant="ghost" to="/register?role=chef">
                Sell to planners
              </Style.Cta>
            </Style.PlanBandActions>
          </Style.PlanBandCopy>
          <Style.PlanBandMedia aria-hidden>
            <img src="/seed/thumbs/dish/jollof-768.webp" alt="Fresh Nigerian Jollof rice platter" />
          </Style.PlanBandMedia>
        </Style.PlanBand>

        {(chefsLoading || chefs.length > 0) && (
          <Style.Section
            eyebrow="Meet the chefs"
            title="Real people, real kitchens."
            description="See the chefs near you and what they make best."
            actions={
              <Style.Cta data-variant="ghost" to="/kitchens">
                View all kitchens →
              </Style.Cta>
            }
          >
            <Style.Grid>
              {chefsLoading
                ? [0, 1, 2, 3].map((i) => <MinimalSkeleton key={i} height="280px" />)
                : chefs.map((chef) => (
                    <Style.Tile key={chef.id} to={kitchenPath(chef)}>
                      <Style.Media>
                        <ChowThumb
                          src={chefPhoto(chef.avatar, chef.cuisine, chef.name)}
                          alt={chef.name}
                          name={chef.name}
                          category={chef.cuisine}
                          thumbnailWidth={384}
                          isChef
                        />
                      </Style.Media>
                      <Style.TileBody>
                        <Style.TileFoot>
                          <h3>{chef.name}</h3>
                          <KitchenBadge tier={chef.badge} />
                        </Style.TileFoot>
                        <p className="chef">
                          {chef.cuisine || 'Home kitchen'}
                          {chef.area ? ` · ${chef.area}` : ''}
                          {distanceNote(chef.distance_km)}
                        </p>
                        <Style.TileFoot>
                          <ChefFootStyle.Open $open={chef.open}>
                            {chef.open ? '● Open now' : '○ Pre-order only'}
                          </ChefFootStyle.Open>
                          <ChefFootStyle.Link>View dishes →</ChefFootStyle.Link>
                        </Style.TileFoot>
                      </Style.TileBody>
                    </Style.Tile>
                  ))}
            </Style.Grid>
          </Style.Section>
        )}

        {(dishesLoading || dishes.length > 0) && (
          <Style.Section
            eyebrow="From local kitchens"
            title="What’s cooking near you"
            description="Every dish shows who made it and what it costs."
            actions={
              <Style.Cta data-variant="ghost" to="/dishes">
                Explore all dishes →
              </Style.Cta>
            }
          >
            <Style.CategoryRail role="tablist" aria-label="Dish categories">
              {CATEGORIES.map((cat) => (
                <Style.CategoryPill
                  key={cat.id}
                  role="tab"
                  aria-selected={selectedCategory === cat.id}
                  data-active={selectedCategory === cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                >
                  {cat.label}
                </Style.CategoryPill>
              ))}
            </Style.CategoryRail>

            <Style.Grid>
              {dishesLoading
                ? [0, 1, 2, 3, 4, 5].map((i) => <MinimalSkeleton key={i} height="280px" />)
                : filteredDishes.map((dish, idx) => (
                    <Style.Tile key={dish.id} to={dishPath(dish)}>
                      <Style.Media>
                        {idx < 2 && (
                          <Style.CardBadge data-variant="bestseller">Bestseller</Style.CardBadge>
                        )}
                        {idx >= 2 && idx < 5 && (
                          <Style.CardBadge data-variant="popular">Popular</Style.CardBadge>
                        )}
                        {Boolean(dish.discount_percent && dish.discount_percent > 0) && (
                          <Style.DiscountBadge>-{dish.discount_percent}%</Style.DiscountBadge>
                        )}
                        <ChowThumb
                          src={dishPhoto(dish.image, dish.category, dish.name)}
                          alt={dish.name}
                          name={dish.name}
                          category={dish.category}
                        />
                      </Style.Media>
                      <Style.TileBody>
                        <h3>{dish.name}</h3>
                        <p className="chef">{dish.chef}</p>
                        <Style.TileFoot>
                          <Style.PriceRow>
                            <span className="price">
                              ₦{(dish.price_minor / 100).toLocaleString()}
                            </span>
                            <span className="rating">
                              {dish.reviews > 0
                                ? `★ ${dish.rating.toFixed(1)} (${dish.reviews})`
                                : 'New'}
                            </span>
                          </Style.PriceRow>
                          <Style.AddBtn aria-hidden="true">+</Style.AddBtn>
                        </Style.TileFoot>
                      </Style.TileBody>
                    </Style.Tile>
                  ))}
            </Style.Grid>
          </Style.Section>
        )}

        <Style.Section
          eyebrow="How it works"
          title="Three steps to your next meal"
        >
          <Style.Steps>
            {STEPS.map((step, i) => (
              <li key={step.title}>
                <span>0{i + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </li>
            ))}
          </Style.Steps>
        </Style.Section>
        <Style.ChefBanner>
          <Style.ChefBannerContent>
            <Style.ChefBannerEyebrow>For home chefs</Style.ChefBannerEyebrow>
            <Style.ChefBannerTitle>Get paid for the food you already cook.</Style.ChefBannerTitle>
            <Style.ChefBannerText>
              Put your dishes in front of people nearby. We take the orders and the payments, so you
              can focus on cooking.
            </Style.ChefBannerText>
            <Style.Cta to="/register?role=chef">Start selling →</Style.Cta>
          </Style.ChefBannerContent>
          <Style.ChefBannerMedia>
            <BrandMascot size={230} />
          </Style.ChefBannerMedia>
        </Style.ChefBanner>
      </Style.Main>
      <PublicFooter />
    </Style.Page>
  )
}
